import * as React from 'react';
import { token } from './theme';

/*
 * PageBackground — full-viewport centered shell for the non-board pages (Home,
 * Lobby). Draws the felt-blue radial gradient and a faint decorative suit grid,
 * then centers its children on top. Keeps the two entry pages visually in sync
 * from one place instead of duplicating the chrome per page.
 */

const SUIT_GLYPHS = ['♠', '♥', '♦', '♣'] as const;

/** Red for hearts/diamonds (index 1,2), text color for spades/clubs. */
const suitColor = (i: number): string =>
  i % 4 === 1 || i % 4 === 2 ? token('color-lose') : token('color-text');

interface PageBackgroundProps {
  children?: React.ReactNode;
}

export const PageBackground: React.FC<PageBackgroundProps> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: `radial-gradient(ellipse at 60% 40%, ${token(
        'color-surface',
      )} 0%, ${token('color-bg')} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: token('font-sans'),
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {Array.from({ length: 12 }).map((_, i) => (
        <span
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '160px',
            color: token('color-text'),
            opacity: 0.025,
          }}
        >
          {SUIT_GLYPHS[i % 4]}
        </span>
      ))}
    </div>
    {children}
  </div>
);

export default PageBackground;

/** Shared by Home & Lobby for the accent suit glyphs on panels. */
export { SUIT_GLYPHS, suitColor };
